import type { Media, Shop } from '@prisma/client'
import { useState, useCallback, useEffect } from 'react'

import { makeShopOwnPath } from '~/helpers/routes'

export function useMediaFetching(shop: Shop) {
  const [media, setMedia] = useState<Media[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [pageToFetch, setPageToFetch] = useState<number>(2)
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const fetchMedia = useCallback(
    async (page = 1) => {
      setIsFetching(true)
      try {
        const url = `${makeShopOwnPath(shop, '/medias')}${
          page > 1 ? `?page=${page}` : ''
        }`
        const request = await fetch(url)
        const response = await request.json()

        if (page === 1) {
          setMedia(response.medias)
        } else {
          setMedia((old) => [...old, ...response.medias])
        }

        setTotalCount(response.totalCount)
        setPageToFetch(page + 1)
      } catch (error) {
        console.error(`medias fetch: ${error}`)
      } finally {
        setIsFetching(false)
      }
    },
    [shop]
  )

  const addMedia = useCallback((newMedia: Media[]) => {
    setMedia((old) => [...newMedia, ...old])
  }, [])

  const removeMedia = useCallback((mediaId: string) => {
    setMedia((old) => old.filter((m) => m.id !== mediaId))
  }, [])

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement
      const scrollEnded =
        target.scrollHeight - (target.scrollTop + target.clientHeight) < 1

      if (scrollEnded && media.length < totalCount && !isFetching) {
        fetchMedia(pageToFetch)
      }
    },
    [media.length, totalCount, isFetching, pageToFetch, fetchMedia]
  )

  // Fetch initial media on mount
  useEffect(() => {
    fetchMedia(1)
  }, [fetchMedia])

  return {
    media,
    totalCount,
    isFetching,
    addMedia,
    removeMedia,
    handleScroll,
    refetch: () => fetchMedia(1),
  }
}
